/**
 * Register website values -> a link the browser can actually follow.
 *
 * `ArEntity.webUrl` mirrors the register's WWW communication channel, which is
 * free text typed by whoever filed the company. More than half of the rows that
 * have one carry no scheme at all ("www.helmes.ee", "kerneki.ee"), and
 * `href="www.helmes.ee"` is a RELATIVE path: the browser resolves it against
 * the CRM itself and the row's website link lands on
 * https://crm.webadmin.ee/www.helmes.ee. It looks like a link, it is blue, and
 * it goes nowhere.
 *
 * The rest of the column is worse than a missing scheme. A live sample of the
 * mirror holds "http:// www.vernissage.ee" (a scheme, then a space), two
 * addresses joined by a conjunction, two joined by a comma, a scheme repeated
 * before the host, plain e-mail addresses typed into the website field
 * ("ando@muffin.ee"), and IDN hosts carrying accented letters.
 *
 * Parsing mirrors `hostFromInput` in
 * apps/api/src/modules/ariregister/lib/domain/suffix.ts - the same values
 * already feed the DNS check and the site scanner - with two deliberate
 * differences:
 *   - the path is kept, because a link should land where the register points;
 *   - an e-mail address is refused instead of read as userinfo. Turning
 *     "alafonte@gmail.com" into "gmail.com" is a fine guess for a domain probe
 *     and a useless company link.
 */

/** Several addresses in one field are joined by these. */
const SEPARATORS = /[\s,;|]+/;

/** IPv4 literal: no company behind it, never a website link. */
const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;

export interface WebLink {
  /** Absolute URL, safe to put in `href`. */
  href: string;
  /** Host without `www.`, scheme or path - the short form a list cell shows. */
  label: string;
}

/**
 * The first usable website in a register value, or null when it holds none:
 * empty, a placeholder word, a bare e-mail address, an IP.
 */
export function parseWebUrl(raw: string | null | undefined): WebLink | null {
  const value = typeof raw === "string" ? raw.trim() : "";
  if (!value) return null;
  for (const token of value.split(SEPARATORS)) {
    const link = fromToken(token);
    if (link) return link;
  }
  return null;
}

/** For callers that need nothing but the `href` value. */
export function webHref(raw: string | null | undefined): string | null {
  return parseWebUrl(raw)?.href ?? null;
}

function fromToken(token: string): WebLink | null {
  // An explicit http:// survives: a handful of registered sites still answer on
  // port 80 only, and upgrading them silently would break a link that works
  // today. Any other scheme (ftp:, mailto:, a typo) is dropped and the address
  // is tried over https.
  const explicit = /^(https?):\/\//i.exec(token);
  const rest = (explicit ? token.slice(explicit[0].length) : token.replace(/^[a-z][a-z0-9+.-]*:\/\//i, ""))
    .replace(/^\/+/, "");
  if (!rest) return null;

  // Split before parsing so the trailing root dot goes away first: URL keeps
  // "helmes.ee." as the hostname and the checks below would read it as broken.
  const cut = rest.search(/[/?#]/);
  const authority = (cut === -1 ? rest : rest.slice(0, cut)).replace(/\.+$/, "").toLowerCase();
  const path = cut === -1 ? "" : rest.slice(cut);

  let parsed: URL;
  try {
    parsed = new URL(`https://${authority}`);
  } catch {
    return null;
  }
  // "ando@muffin.ee" parses as userinfo plus host. See the file header: the
  // token is refused so the next one in the field gets its turn.
  if (parsed.username || parsed.password) return null;

  const host = parsed.hostname;
  if (!host.includes(".") || host.startsWith(".")) return null;
  if (IPV4.test(host)) return null;

  // The href is built from the text, not from `parsed.href`, so an IDN host
  // stays readable in the label. Browsers punycode it on navigation anyway.
  const scheme = explicit ? explicit[1]!.toLowerCase() : "https";
  return { href: `${scheme}://${authority}${path}`, label: authority.replace(/^www\./, "") };
}
